// How many metres a fork may take off a drum that already has children.
//
// ── the report ──────────────────────────────────────────────────────────────
//
// 2026-10-30, CYYF 3x2.5 (job 6ae06d6042dbcd6c20d82a22). Three insulated cores
// were each run long and the rope took only part of them:
//
//   K-7b3947ff  produced 10 024.5  consumed 5 436.9  available 4 587.6
//   K-ece917ff  produced 10 031.0  consumed 5 410.4  available 4 620.6
//   K-8c767648  produced 10 024.0  consumed 5 462.9  available 4 561.1
//
// The rope task is COMPLETED, so nothing will ever claim those metres. Project
// Token State shows them as free and unprogrammed. The transform wizard listed
// them as free too — and then refused all three:
//
//   "3 of 3 ticked drums will NOT be transformed — nothing would move.
//    K-8c767648 (BLUE) — already consumed by rope in CYYF."
//
// `forkProduct` was refusing on the EXISTENCE of a downstream consumer:
//
//   const consumers = tasks.filter(t => t.inputs?.includes(stid));
//   if (consumers.length) { warnings.push("already consumed … skipped"); continue; }
//
// A drum that gave 5 436 m to a rope and kept 4 587 m has a consumer, so it was
// unforkable for ever — however much was left on it. The screen said "4,537 m
// free" and "skipped" in the same row, which is how the contradiction surfaced.
//
// ── why the blanket rule could not simply be deleted ────────────────────────
//
// It was standing in for something real. `tokenEffective` just above it uses
// `max(reservedLength, consumedLength)`, and **`reservedLength` is not always
// set**: a plan-ahead or chained plan books a child without bumping it (see the
// note in controller/bobins.js). On such a drum `effective` overstates what is
// free, and the consumer check was the only thing stopping the fork from
// selling the same metres twice.
//
// So the fix is to make the check QUANTITATIVE rather than to remove it: ask
// what the children actually claim, and refuse only the metres beyond what is
// left.
//
// ── the rule ────────────────────────────────────────────────────────────────
//
//   held    = produced > 0 ? availableLength : plannedLength
//   pending = what children that are NOT yet produced claim from this drum
//   forward = reservedLength beyond what has already been drawn
//   free    = max(0, held - max(pending, forward))
//
//  · `held` is the figure the system maintains — `available` is already
//    `produced - consumed`, including the over-run (10 024.5, not the 10 000
//    plan), which is why it is used instead of reconstructing a capacity.
//  · a child's claim is `parentTakes[thisDrum]`, falling back to the child's
//    whole planned length when it is absent — without that fallback a 25 km run
//    would count as nothing against a drum it drew 5 km from.
//  · children that are already DONE are NOT counted: their draw is in
//    `consumedLength`, and counting both would charge the drum twice.
//  · a child RUNNING on a machine counts for what it STILL HAS TO MAKE. This
//    was the sharpest edge of the lot — see the note on `childStage`.
//  · `max`, never a sum, for the same reason: `reserved` and `consumed` describe
//    the same metres at two different moments.
//
// Pure and dependency-free, so it can be byte-identical on both sides. The web
// wizard carries the same rule in `helpers/transformDrums.js` so the preview and
// the endpoint cannot disagree about what will move.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const DONE = new Set(["DONE", "COMPLETED", "EXECUTED", "FINISHED"]);
const RUNNING = new Set(["ACTIVE", "RUNNING", "IN_PROGRESS"]);

/** The task that makes this drum. */
function producingTask(tasks, tokenId) {
  return (
    Object.values(tasks || {}).find(
      (t) => Array.isArray(t?.outputs) && t.outputs.includes(String(tokenId))
    ) || null
  );
}

/**
 * Where a child is in its life, which decides how much of its parent it still
 * has a hold on.
 *
 *   done     finished — its whole draw is already in the parent's `consumedLength`
 *   running  ON A MACHINE RIGHT NOW, part-made: it still needs the remainder
 *   queued   nothing made: it needs its whole planned take
 *
 * ── why `producedLength > 0` is NOT "done" ──────────────────────────────────
 *
 * It used to be, and that was wrong in the one direction that matters. Flagged
 * 2026-10-30: *"un rope poate fi activ pe masina dar e foarte important din ce
 * tokeni de conductor isolation isi trage sa faca ropeul activ."*
 *
 * Found live on MYF 1x2.5 the same day: a coiler RUNNING on ach300, 3 813 m of a
 * 10 000 m plan made, so still owed 6 187 m — and its input drum read
 * `consumed 0`, `reserved 0`, `available 10 000.4`. Treating the part-made child
 * as finished skipped its claim entirely, so the fork would have offered the
 * WHOLE drum while the machine was drawing from it.
 *
 * `reservedLength` cannot be relied on to catch it: it was 0 on both live
 * examples.
 */
function childStage(child, tokenId, tasks) {
  const task = producingTask(tasks, tokenId);
  const taskStatus = String(task?.status || "").toUpperCase();
  const tokStatus = String(child?.status || "").toUpperCase();

  if (RUNNING.has(taskStatus) || RUNNING.has(tokStatus)) return "running";
  if (DONE.has(taskStatus) || DONE.has(tokStatus)) return "done";

  // Metres on it and NO status anywhere to explain them. Called "done" at
  // first, which is the unsafe guess: it claims nothing from the parent. Read
  // as mid-run instead, so the remainder is claimed — and on a drum that really
  // did finish the remainder is ~0, so the safe reading costs nothing. Same
  // principle as the uncapped claim: over-reserving refuses a fork that might
  // have been fine, under-reserving pulls cable from a machine.
  if (num(child?.producedLength) > 0) return "running";
  return "queued";
}

/**
 * What the children of `tokenId` that are NOT finished still count on it for.
 *
 * A `queued` child claims its whole planned take. A `running` child claims only
 * what it still has to make — it has already drawn the rest, and that part is in
 * the parent's `consumedLength`.
 *
 * ── the running case, which is the dangerous one ────────────────────────────
 *
 * *"un rope poate fi activ pe masina dar e foarte important din ce tokeni de
 * conductor isolation isi trage sa faca ropeul activ."*
 *
 * A rope turning on skip2250 right now is drawing from specific cores, and
 * those metres are not free however the fields read. `parentTakes` caps the
 * claim when it is there; without it the child's whole remaining need is
 * charged to EVERY parent it names. That over-reserves when two drums of one
 * colour feed it in sequence — deliberately, because the two errors are not
 * equal: over-reserving refuses a fork that might have been fine, while
 * under-reserving pulls cable out from under a running machine.
 *
 * @returns `{ metres, children: [{ tokenId, phase, metres, stage, machineId }] }`
 */
export function pendingChildClaim(job, tokenId) {
  const id = String(tokenId || "");
  const tokens = job?.tokens && typeof job.tokens === "object" ? job.tokens : {};
  const tasks = job?.tasks && typeof job.tasks === "object" ? job.tasks : {};

  let metres = 0;
  const children = [];

  for (const [childId, child] of Object.entries(tokens)) {
    if (!child || childId === id) continue;
    const parents = Array.isArray(child.parents) ? child.parents.map(String) : [];
    if (!parents.includes(id)) continue;

    const stage = childStage(child, childId, tasks);
    // Finished → its whole draw is inside the parent's `consumedLength` already.
    if (stage === "done") continue;

    const takes =
      child.parentTakes && typeof child.parentTakes === "object" ? child.parentTakes : null;
    const planned = takes && Number.isFinite(Number(takes[id]))
      ? num(takes[id])
      : num(child.plannedLength);

    // Mid-run: only the remainder is still owed. Capped by this parent's own
    // planned share when one is recorded.
    const claim =
      stage === "running"
        ? Math.min(planned, Math.max(0, num(child.plannedLength) - num(child.producedLength)))
        : planned;

    if (claim <= 0) continue;
    metres += claim;
    children.push({
      tokenId: childId,
      phase: child.phase || null,
      metres: Math.round(claim * 1000) / 1000,
      stage,
      machineId: producingTask(tasks, childId)?.machineId || null,
    });
  }

  return { metres: Math.round(metres * 1000) / 1000, children };
}

/**
 * The metres a fork may take off this drum, and the arithmetic behind it.
 *
 * Returned as an object rather than a bare number because the refusal has to be
 * explainable on screen — "already consumed" was true and useless.
 */
export function forkFreeLength(job, tokenId) {
  const tok = job?.tokens?.[String(tokenId)] || null;
  if (!tok) return { free: 0, held: 0, pending: 0, forward: 0, children: [], missing: true };

  const produced = num(tok.producedLength);
  const held = produced > 0 ? num(tok.availableLength) : num(tok.plannedLength);

  const { metres: pending, children } = pendingChildClaim(job, tokenId);
  // Reservations beyond what has actually been drawn. A reservation that has
  // since been consumed is the same metres counted once, not twice.
  const forward = Math.max(0, num(tok.reservedLength) - num(tok.consumedLength));

  const committed = Math.max(pending, forward);
  const free = Math.max(0, held - committed);

  const running = children.filter((c) => c.stage === "running");

  return {
    free: Math.round(free * 1000) / 1000,
    held: Math.round(held * 1000) / 1000,
    pending,
    forward: Math.round(forward * 1000) / 1000,
    children,
    // The children that are ON A MACHINE now. Surfaced separately because the
    // advice differs: a queued child can be un-planned, a turning one has to be
    // stopped first, and the planner needs the machine named to do either.
    running,
    missing: false,
  };
}

export default { pendingChildClaim, forkFreeLength };
